import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  Switch,
  Autocomplete,
  Pagination,
  Checkbox,
  Badge,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  MarkEmailRead as ReadIcon,
  MarkEmailUnread as UnreadIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Pets as PetsIcon,
  FilterList as FilterIcon,
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from "@mui/icons-material";

import {
  INotification,
  ICreateNotification,
  IUpdateNotification,
  INotificationFilters,
  IPetForBackend,
} from "../types";
import {
  createNotification,
  getNotifications,
  getNotificationById,
  updateNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  getPets,
} from "../services";
import { useNotifications } from "../contexts/NotificationContext";
import {
  getBestPhoneNumber,
  generateNotificationTemplate,
  sendWhatsAppMessage,
  sendBulkWhatsAppMessages,
  WhatsAppMessage,
} from "../services/whatsappApi";
import WhatsAppConfig from "../components/WhatsAppConfig";

interface NotificationsProps {
  isMobile?: boolean;
  isSmallScreen?: boolean;
}

const Notifications: React.FC<NotificationsProps> = ({
  isMobile = false,
  isSmallScreen = false,
}) => {
  // Hook do contexto para atualização automática
  const { refreshNotifications, lastUpdate } = useNotifications();

  // Estados principais
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [loading, setLoading] = useState(false);

  // Estado para pets
  const [allPets, setAllPets] = useState<IPetForBackend[]>([]); // Cache de todos os pets
  const [selectedPet, setSelectedPet] = useState<IPetForBackend | null>(null);
  const [petInputValue, setPetInputValue] = useState("");

  // Estados dos modais
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<INotification | null>(null);

  // Estados dos formulários
  const [formData, setFormData] = useState<{
    title: string;
    summary: string;
    details: string;
    scheduledDate: string;
    scheduledTime: string;
    petId: string;
  }>({
    title: "",
    summary: "",
    details: "",
    scheduledDate: "",
    scheduledTime: "",
    petId: "",
  });

  // Estados de filtros
  // Função para obter a data de hoje no formato YYYY-MM-DD
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Função para obter a data de amanhã (para incluir o dia inteiro no filtro)
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  const [filters, setFilters] = useState<INotificationFilters>({
    page: 1,
    limit: 100,
    isActive: true,
    startDate: getTodayDate(),
    endDate: getTomorrowDate(), // Usa amanhã para incluir o dia inteiro de hoje
  });
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);

  // Estados temporários para os filtros (antes de aplicar)
  const [tempStartDate, setTempStartDate] = useState(getTodayDate());
  const [tempEndDate, setTempEndDate] = useState(getTodayDate());
  const [tempShowOnlyUnread, setTempShowOnlyUnread] = useState(false);

  // Estados de paginação
  const [notificationsPage, setNotificationsPage] = useState(1);
  const notificationsPerPage = 6; // 6 notificações por página

  // Estados de feedback
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });
  const [error, setError] = useState<string | null>(null);

  const [shouldPoll, setShouldPoll] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  // Estados do WhatsApp
  const [selectedForWhatsApp, setSelectedForWhatsApp] = useState<string[]>([]);
  const [openWhatsAppModal, setOpenWhatsAppModal] = useState(false);
  const [whatsAppMessages, setWhatsAppMessages] = useState<
    {
      id: string;
      phone: string;
      text: string;
      tutorName: string;
      petName: string;
      notificationTitle: string;
    }[]
  >([]);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [whatsAppSingleMessage, setWhatsAppSingleMessage] = useState<{
    notification: INotification | null;
    phone: string;
    text: string;
  }>({ notification: null, phone: "", text: "" });
  const [openSingleWhatsAppModal, setOpenSingleWhatsAppModal] = useState(false);
  const [whatsAppConnected, setWhatsAppConnected] = useState(false);

  // Estados de estatísticas de disparo WhatsApp
  const [dispatchStats, setDispatchStats] = useState<{
    total: number;
    sent: number;
    failed: number;
    pending: number;
  } | null>(null);
  const [loadingDispatch, setLoadingDispatch] = useState(false);

  // API base URL
  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:3001/api";

  // Função auxiliar para extrair nome do tutor
  const getTutorName = (tutor: any): string => {
    if (!tutor) return "";

    if (typeof tutor === "string") {
      return tutor;
    }

    if (tutor && typeof tutor === "object") {
      const name =
        tutor.name ||
        tutor.NomeFantasia ||
        tutor.RazaoSocial ||
        tutor.tutorName ||
        (tutor.tutor && typeof tutor.tutor === "string" ? tutor.tutor : "") ||
        "Nome não disponível";
      return name;
    }

    return "";
  };

  // Função auxiliar para verificar se a notificação tem telefone do tutor
  const hasWhatsAppPhone = (notification: INotification): boolean => {
    const owner = notification.pet?.owner;
    return !!getBestPhoneNumber(owner);
  };

  // Função para obter o telefone do tutor
  const getNotificationPhone = (notification: INotification): string => {
    const owner = notification.pet?.owner;
    return getBestPhoneNumber(owner) || "";
  };

  // Função para abrir modal de WhatsApp individual
  const handleOpenSingleWhatsApp = (notification: INotification) => {
    const owner = notification.pet?.owner;
    const phone = getBestPhoneNumber(owner) || "";
    const tutorName =
      owner?.name || getTutorName(notification.pet?.tutor) || "Cliente";
    const petName = notification.pet?.name || "Pet";
    const text = generateNotificationTemplate(
      tutorName,
      petName,
      notification.title,
      notification.summary
    );

    setWhatsAppSingleMessage({ notification, phone, text });
    setOpenSingleWhatsAppModal(true);
  };

  // Função para enviar mensagem individual
  const handleSendSingleWhatsApp = async () => {
    if (!whatsAppSingleMessage.phone || !whatsAppSingleMessage.text) return;

    setSendingWhatsApp(true);
    try {
      const result = await sendWhatsAppMessage(
        whatsAppSingleMessage.phone,
        whatsAppSingleMessage.text
      );

      if (result.success) {
        setSnackbar({
          open: true,
          message: "Mensagem enviada com sucesso!",
          severity: "success",
        });
        setOpenSingleWhatsAppModal(false);

        // Marcar notificação como lida após envio
        if (whatsAppSingleMessage.notification) {
          await markNotificationAsRead(whatsAppSingleMessage.notification.id);
          loadNotifications();
        }
      } else {
        setSnackbar({
          open: true,
          message: result.error || "Erro ao enviar mensagem",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Erro ao enviar mensagem",
        severity: "error",
      });
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Função para toggle de seleção de notificação para WhatsApp
  const handleToggleWhatsAppSelection = (notificationId: string) => {
    setSelectedForWhatsApp((prev) =>
      prev.includes(notificationId)
        ? prev.filter((id) => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  // Função para selecionar todas as notificações não lidas com telefone
  const handleSelectAllForWhatsApp = () => {
    const eligibleIds = filteredNotifications
      .filter((n) => !n.isRead && hasWhatsAppPhone(n))
      .map((n) => n.id);

    if (selectedForWhatsApp.length === eligibleIds.length) {
      setSelectedForWhatsApp([]);
    } else {
      setSelectedForWhatsApp(eligibleIds);
    }
  };

  // Função para selecionar todas de HOJE para disparo
  const handleSelectAllTodayForWhatsApp = () => {
    const today = getTodayDate();
    const eligibleIds = notifications
      .filter((n) => {
        const notificationDate = new Date(n.scheduledDate)
          .toISOString()
          .split("T")[0];
        return (
          notificationDate === today && hasWhatsAppPhone(n) && !n.whatsappSent // Apenas as que ainda não foram enviadas
        );
      })
      .map((n) => n.id);

    if (selectedForWhatsApp.length === eligibleIds.length) {
      setSelectedForWhatsApp([]);
    } else {
      setSelectedForWhatsApp(eligibleIds);
    }
  };

  // Função para buscar estatísticas de disparo do dia
  const fetchDispatchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/notifications/whatsapp/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setDispatchStats(data.data);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar estatísticas de disparo:", error);
    }
  };

  // Função para disparar mensagens do dia manualmente
  const handleTriggerTodayDispatch = async () => {
    setLoadingDispatch(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/notifications/whatsapp/dispatch-today`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setSnackbar({
          open: true,
          message: `Disparo concluído: ${data.data.sent} enviadas, ${data.data.failed} falhas`,
          severity: data.data.failed > 0 ? "warning" : "success",
        });
        // Recarregar notificações e estatísticas
        refreshNotifications();
        fetchDispatchStats();
      } else {
        setSnackbar({
          open: true,
          message: data.error || "Erro ao disparar mensagens",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Erro ao disparar mensagens",
        severity: "error",
      });
    } finally {
      setLoadingDispatch(false);
    }
  };

  // Função para abrir modal de envio em massa
  const handleOpenBulkWhatsApp = () => {
    const messages = selectedForWhatsApp
      .map((id) => {
        const notification = notifications.find((n) => n.id === id);
        if (!notification) return null;

        const owner = notification.pet?.owner;
        const phone = getBestPhoneNumber(owner);
        if (!phone) return null;

        const tutorName =
          owner?.name || getTutorName(notification.pet?.tutor) || "Cliente";
        const petName = notification.pet?.name || "Pet";
        const text = generateNotificationTemplate(
          tutorName,
          petName,
          notification.title,
          notification.summary
        );

        return {
          id,
          phone,
          text,
          tutorName,
          petName,
          notificationTitle: notification.title,
        };
      })
      .filter((msg): msg is NonNullable<typeof msg> => msg !== null);

    setWhatsAppMessages(messages);
    setOpenWhatsAppModal(true);
  };

  // Função para atualizar mensagem individual no modal bulk
  const handleUpdateBulkMessage = (id: string, newText: string) => {
    setWhatsAppMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, text: newText } : msg))
    );
  };

  // Função para enviar mensagens em massa
  const handleSendBulkWhatsApp = async () => {
    if (whatsAppMessages.length === 0) return;

    setSendingWhatsApp(true);
    try {
      const messagesToSend: WhatsAppMessage[] = whatsAppMessages.map((msg) => ({
        id: msg.id,
        phone: msg.phone,
        text: msg.text,
      }));

      const result = await sendBulkWhatsAppMessages(messagesToSend);

      const successCount = result.results.filter((r) => r.success).length;
      const failCount = result.results.filter((r) => !r.success).length;

      if (successCount > 0) {
        setSnackbar({
          open: true,
          message: `${successCount} mensagem(ns) enviada(s) com sucesso${failCount > 0 ? `, ${failCount} falha(s)` : ""}!`,
          severity: successCount > 0 && failCount === 0 ? "success" : "warning",
        });

        // Marcar notificações enviadas como lidas
        const successIds = result.results
          .filter((r) => r.success)
          .map((r) => String(r.id));

        for (const id of successIds) {
          await markNotificationAsRead(id);
        }

        loadNotifications();
        setSelectedForWhatsApp([]);
        setOpenWhatsAppModal(false);
      } else {
        setSnackbar({
          open: true,
          message: "Erro ao enviar mensagens",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Erro ao enviar mensagens",
        severity: "error",
      });
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Filtro instantâneo usando useMemo - ZERO delay
  const filteredPets = useMemo(() => {
    if (!petInputValue || petInputValue.length < 1) {
      return allPets.slice(0, 50); // Limitar para melhor performance
    }

    const searchTerm = petInputValue.toLowerCase();
    return allPets
      .filter((pet: IPetForBackend) =>
        pet.name.toLowerCase().includes(searchTerm)
      )
      .slice(0, 20); // Limitar resultados
  }, [allPets, petInputValue]);

  // Carregar pets
  const loadPets = useCallback(async () => {
    try {
      const petsData = await getPets();
      setAllPets(petsData); // Salva no cache
    } catch (error) {
      setAllPets([]);
    }
  }, []);

  // Carregar notificações
  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getNotifications(filters);
      const notificationsData = response.data;

      // Enriquecer notificações com dados completos dos pets (incluindo tutor)
      const enrichedNotifications = await Promise.all(
        notificationsData.map(async (notification: INotification) => {
          // Se há um pet mas sem dados do tutor, tentar buscar o pet completo
          if (
            notification.pet &&
            !notification.pet.tutor &&
            notification.petId &&
            allPets.length > 0
          ) {
            const petWithTutor = allPets.find(
              (p: IPetForBackend) => p.id === notification.petId
            );
            if (petWithTutor && petWithTutor.tutor) {
              return {
                ...notification,
                pet: {
                  ...notification.pet,
                  tutor: petWithTutor.tutor,
                },
              };
            }
          }
          return notification;
        })
      );

      setNotifications(enrichedNotifications);
    } catch (error: any) {
      setError("Erro ao carregar notificações");
      setSnackbar({
        open: true,
        message: "Erro ao carregar notificações",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [filters, allPets]);

  // Carregar pets inicialmente para ter opções disponíveis
  useEffect(() => {
    loadPets();
  }, [loadPets]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Buscar estatísticas de disparo do dia ao carregar
  useEffect(() => {
    fetchDispatchStats();
  }, [notifications]); // Atualiza quando notificações mudam

  // Recarregar notificações quando a página recebe foco ou fica visível
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadNotifications();
      }
    };

    const handleFocus = () => {
      loadNotifications();
    };

    // Listeners para detectar quando usuário volta para a página
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [loadNotifications]);

  // Polling inteligente: recarrega por 30 segundos após mudanças importantes
  useEffect(() => {
    if (!shouldPoll) return;

    const maxPolls = 3; // 3 polls = 30 segundos (a cada 10s)
    if (pollCount >= maxPolls) {
      setShouldPoll(false);
      setPollCount(0);
      return;
    }

    const timer = setTimeout(() => {
      loadNotifications();
      setPollCount((prev) => prev + 1);
    }, 10000); // Poll a cada 10 segundos

    return () => clearTimeout(timer);
  }, [shouldPoll, pollCount, loadNotifications]);

  // Observer: detecta mudanças externas via contexto (ex: exclusão de agendamento)
  useEffect(() => {
    loadNotifications();
    // Ativar polling por alguns segundos para garantir sincronização
    setShouldPoll(true);
    setPollCount(0);
  }, [lastUpdate, loadNotifications]);

  // Resetar formulário
  const resetForm = () => {
    setFormData({
      title: "",
      summary: "",
      details: "",
      scheduledDate: "",
      scheduledTime: "",
      petId: "",
    });
    setSelectedPet(null);
    setPetInputValue("");
  };

  // Handlers dos modais
  const handleOpenCreateModal = () => {
    resetForm();
    setOpenCreateModal(true);
  };

  const handleOpenEditModal = async (notification: INotification) => {
    try {
      const fullNotification = await getNotificationById(notification.id);
      setSelectedNotification(fullNotification);
      setFormData({
        title: fullNotification.title,
        summary: fullNotification.summary,
        details: fullNotification.details || "",
        scheduledDate: fullNotification.scheduledDate.split("T")[0],
        scheduledTime: fullNotification.scheduledTime,
        petId: fullNotification.petId || "",
      });

      // Configurar o pet selecionado se houver
      if (fullNotification.pet) {
        const petData: IPetForBackend = {
          id: fullNotification.pet.id,
          name: fullNotification.pet.name,
          breed: fullNotification.pet.breed,
          photo: fullNotification.pet.photo,
          tutor: fullNotification.pet.tutor,
        };
        setSelectedPet(petData);
        setPetInputValue(fullNotification.pet.name);
      } else {
        setSelectedPet(null);
        setPetInputValue("");
      }
      setOpenEditModal(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Erro ao carregar detalhes da notificação",
        severity: "error",
      });
    }
  };

  const handleOpenViewModal = async (notification: INotification) => {
    try {
      const fullNotification = await getNotificationById(notification.id);

      // Se há um pet mas sem dados do tutor, tentar buscar o pet completo
      if (
        fullNotification.pet &&
        !fullNotification.pet.tutor &&
        fullNotification.petId
      ) {
        try {
          const allPetsData = await getPets();
          const petWithTutor = allPetsData.find(
            (p: IPetForBackend) => p.id === fullNotification.petId
          );
          if (petWithTutor) {
            fullNotification.pet = {
              ...fullNotification.pet,
              tutor: petWithTutor.tutor,
            };
          }
        } catch (petError) {
          // Não foi possível buscar dados completos do pet, mas continua
        }
      }

      setSelectedNotification(fullNotification);
      setOpenViewModal(true);

      // Marcar como lida se ainda não foi
      // if (!fullNotification.isRead) {
      //   try {
      //     await markNotificationAsRead(fullNotification.id);
      //     loadNotifications();
      //   } catch (readError) {
      //     // Notificação aberta mesmo sem marcar como lida
      //   }
      // }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          "Erro ao visualizar notificação. Você pode não ter permissão para visualizar esta notificação.",
        severity: "error",
      });
    }
  };

  const handleCloseModals = () => {
    setOpenCreateModal(false);
    setOpenEditModal(false);
    setOpenViewModal(false);
    setSelectedNotification(null);
    resetForm();
  };

  // Criar notificação
  const handleCreateNotification = async () => {
    if (
      !formData.title ||
      !formData.summary ||
      !formData.scheduledDate ||
      !formData.scheduledTime
    ) {
      setSnackbar({
        open: true,
        message: "Preencha todos os campos obrigatórios",
        severity: "warning",
      });
      return;
    }

    try {
      // Criar data com horário 12:00:00 para evitar problemas de timezone
      const dateWithNoon = new Date(formData.scheduledDate + "T12:00:00");

      const newNotification: ICreateNotification = {
        title: formData.title,
        summary: formData.summary,
        details: formData.details || undefined,
        scheduledDate: dateWithNoon.toISOString(),
        scheduledTime: formData.scheduledTime,
        petId: selectedPet?.id || undefined,
      };

      await createNotification(newNotification);
      const petMessage = selectedPet ? ` para ${selectedPet.name}` : "";
      setSnackbar({
        open: true,
        message: `Notificação${petMessage} criada com sucesso!`,
        severity: "success",
      });
      handleCloseModals();

      // Atualizar tanto a lista local quanto o contexto global
      await Promise.all([loadNotifications(), refreshNotifications()]);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Erro ao criar notificação",
        severity: "error",
      });
    }
  };

  // Atualizar notificação
  const handleUpdateNotification = async () => {
    if (
      !selectedNotification ||
      !formData.title ||
      !formData.summary ||
      !formData.scheduledDate ||
      !formData.scheduledTime
    ) {
      setSnackbar({
        open: true,
        message: "Preencha todos os campos obrigatórios",
        severity: "warning",
      });
      return;
    }

    try {
      // Criar data com horário 12:00:00 para evitar problemas de timezone
      const dateWithNoon = new Date(formData.scheduledDate + "T12:00:00");

      const updateData: IUpdateNotification = {
        title: formData.title,
        summary: formData.summary,
        details: formData.details || undefined,
        scheduledDate: dateWithNoon.toISOString(),
        scheduledTime: formData.scheduledTime,
        petId: selectedPet?.id || undefined,
      };

      await updateNotification(selectedNotification.id, updateData);
      const petMessage = selectedPet ? ` para ${selectedPet.name}` : "";
      setSnackbar({
        open: true,
        message: `Notificação${petMessage} atualizada com sucesso!`,
        severity: "success",
      });
      handleCloseModals();

      // Atualizar tanto a lista local quanto o contexto global
      await Promise.all([loadNotifications(), refreshNotifications()]);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Erro ao atualizar notificação",
        severity: "error",
      });
    }
  };

  // Deletar notificação
  const handleDeleteNotification = async (id: string) => {
    if (window.confirm("Tem certeza que deseja deletar esta notificação?")) {
      try {
        await deleteNotification(id);
        setSnackbar({
          open: true,
          message: "Notificação deletada com sucesso!",
          severity: "success",
        });

        // Atualizar tanto a lista local quanto o contexto global
        await Promise.all([loadNotifications(), refreshNotifications()]);
      } catch (error: any) {
        setSnackbar({
          open: true,
          message:
            error.message ||
            "Erro ao deletar notificação. Você pode não ter permissão.",
          severity: "error",
        });
      }
    }
  };

  // Marcar como lida/não lida
  const handleToggleRead = async (notification: INotification) => {
    try {
      if (!notification.isRead) {
        await markNotificationAsRead(notification.id);
        setSnackbar({
          open: true,
          message: "Notificação marcada como lida",
          severity: "success",
        });
      } else {
        // Para marcar como não lida, usamos update
        await updateNotification(notification.id, { isRead: false });
        setSnackbar({
          open: true,
          message: "Notificação marcada como não lida",
          severity: "success",
        });
      }

      // Atualizar tanto a lista local quanto o contexto global
      await Promise.all([loadNotifications(), refreshNotifications()]);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message:
          error.message ||
          "Erro ao atualizar status. Você pode não ter permissão.",
        severity: "error",
      });
    }
  };

  // Marcar todas como lidas
  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setSnackbar({
        open: true,
        message: "Todas as notificações foram marcadas como lidas",
        severity: "success",
      });

      // Atualizar tanto a lista local quanto o contexto global
      await Promise.all([loadNotifications(), refreshNotifications()]);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Erro ao marcar todas como lidas",
        severity: "error",
      });
    }
  };

  // Abrir modal de filtros
  const handleOpenFilterModal = () => {
    // Sincronizar valores temporários com os valores atuais dos filtros
    setTempStartDate(filters.startDate || getTodayDate());
    setTempEndDate(filters.endDate || getTodayDate());
    setTempShowOnlyUnread(showOnlyUnread);
    setOpenFilterModal(true);
  };

  // Aplicar filtros
  const handleApplyFilters = () => {
    // Ajustar endDate para incluir o dia inteiro
    // Adiciona 1 dia à data final para garantir que o dia seja incluído na busca
    const adjustedEndDate = new Date(tempEndDate + "T00:00:00");
    adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
    const endDateForFilter = adjustedEndDate.toISOString().split("T")[0];

    setFilters({
      ...filters,
      startDate: tempStartDate,
      endDate: endDateForFilter,
    });
    setShowOnlyUnread(tempShowOnlyUnread);
    setOpenFilterModal(false);
  };

  // Limpar filtros (voltar para o dia de hoje)
  const handleClearFilters = () => {
    const today = getTodayDate();

    // Ajustar endDate para incluir o dia inteiro
    const adjustedEndDate = new Date(today + "T00:00:00");
    adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
    const endDateForFilter = adjustedEndDate.toISOString().split("T")[0];

    setTempStartDate(today);
    setTempEndDate(today);
    setTempShowOnlyUnread(false);
    setFilters({
      ...filters,
      startDate: today,
      endDate: endDateForFilter,
    });
    setShowOnlyUnread(false);
    setOpenFilterModal(false);
  };

  // Formatação de data e hora
  const formatDateTime = (date: string, time: string) => {
    const dateObj = new Date(date);
    return `${dateObj.toLocaleDateString("pt-BR")} às ${time}`;
  };

  // Verificar se a notificação está próxima (próximas 24h)
  const isUpcoming = (date: string, time: string) => {
    const notificationDateTime = new Date(`${date} ${time}`);
    const now = new Date();
    const in24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    return notificationDateTime > now && notificationDateTime < in24Hours;
  };

  // Verificar se a notificação passou do horário
  const isOverdue = (date: string, time: string) => {
    const notificationDateTime = new Date(`${date} ${time}`);
    return notificationDateTime < new Date();
  };

  // Filtrar e ordenar notificações
  const getFilteredNotifications = () => {
    let filtered = [...notifications];

    // Aplicar filtro de não lidas se ativo
    if (showOnlyUnread) {
      filtered = filtered.filter((notification) => !notification.isRead);
    }

    // Ordenar das mais recentes para as mais antigas (por data/hora de agendamento)
    filtered.sort((a, b) => {
      const dateTimeA = new Date(`${a.scheduledDate} ${a.scheduledTime}`);
      const dateTimeB = new Date(`${b.scheduledDate} ${b.scheduledTime}`);
      return dateTimeB.getTime() - dateTimeA.getTime();
    });

    return filtered;
  };

  const filteredNotifications = getFilteredNotifications();

  // Paginação de notificações
  const paginatedNotifications = useMemo(() => {
    const startIndex = (notificationsPage - 1) * notificationsPerPage;
    const endIndex = startIndex + notificationsPerPage;
    return filteredNotifications.slice(startIndex, endIndex);
  }, [filteredNotifications, notificationsPage, notificationsPerPage]);

  const totalNotificationsPages = useMemo(() => {
    return Math.ceil(filteredNotifications.length / notificationsPerPage);
  }, [filteredNotifications.length, notificationsPerPage]);

  // Reset para página 1 quando os filtros mudarem
  useEffect(() => {
    setNotificationsPage(1);
  }, [showOnlyUnread, filters]);

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 0, mt: -4 }}>
        {/* Cabeçalho com título e status do WhatsApp compacto */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            mb: 1,
            gap: isMobile ? 2 : 1,
            px: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {/* <Typography
              variant={isMobile || isSmallScreen ? "h6" : "h4"}
              component="h1"
            >
              Notificações Agendadas
            </Typography> */}
            {/* WhatsApp Status Compacto */}
            <WhatsAppConfig onStatusChange={setWhatsAppConnected} compact />
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: isMobile || isSmallScreen ? 1 : 2,
              alignItems: "center",
              flexWrap: isMobile ? "wrap" : "nowrap",
              justifyContent: isMobile ? "space-between" : "flex-end",
            }}
          >
            <Button
              variant="outlined"
              startIcon={!isMobile && <FilterIcon />}
              onClick={handleOpenFilterModal}
              size={isMobile || isSmallScreen ? "small" : "medium"}
              sx={{ flex: isMobile ? "1 1 auto" : "0 0 auto" }}
            >
              {isMobile ? <FilterIcon /> : "Filtros"}
            </Button>
            <Button
              variant="outlined"
              startIcon={!isMobile && <ReadIcon />}
              onClick={handleMarkAllAsRead}
              size={isMobile || isSmallScreen ? "small" : "medium"}
              sx={{ flex: isMobile ? "1 1 auto" : "0 0 auto" }}
            >
              {isMobile ? <ReadIcon /> : "Marcar Todas como Lidas"}
            </Button>
            <Button
              variant="contained"
              startIcon={!isMobile && <AddIcon />}
              onClick={handleOpenCreateModal}
              size={isMobile || isSmallScreen ? "small" : "medium"}
              sx={{ flex: isMobile ? "1 1 100%" : "0 0 auto" }}
            >
              {isMobile ? <AddIcon /> : "Nova Notificação"}
            </Button>
          </Box>
        </Box>

        {/* Linha do Botão de Enviar WhatsApp em Massa */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            px: { xs: 1, sm: 0 },
          }}
        >
          <Badge
            badgeContent={selectedForWhatsApp.length}
            color="success"
            invisible={selectedForWhatsApp.length === 0}
          >
            <Tooltip
              title={
                !whatsAppConnected
                  ? "WhatsApp desconectado - conecte primeiro"
                  : ""
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<WhatsAppIcon />}
                  onClick={handleOpenBulkWhatsApp}
                  disabled={
                    selectedForWhatsApp.length === 0 || !whatsAppConnected
                  }
                  size={isMobile || isSmallScreen ? "small" : "medium"}
                >
                  {selectedForWhatsApp.length > 0
                    ? `Enviar WhatsApp (${selectedForWhatsApp.length})`
                    : "Enviar WhatsApp"}
                </Button>
              </span>
            </Tooltip>
          </Badge>

          {/* Botão Selecionar Todas de Hoje */}
          <Tooltip
            title={
              !whatsAppConnected
                ? "WhatsApp desconectado"
                : "Selecionar todas as notificações de hoje pendentes de envio"
            }
          >
            <span>
              <Button
                variant="outlined"
                color="success"
                onClick={handleSelectAllTodayForWhatsApp}
                disabled={!whatsAppConnected}
                size={isMobile || isSmallScreen ? "small" : "medium"}
              >
                Selecionar Hoje
              </Button>
            </span>
          </Tooltip>

          {/* Botão Disparar Todas de Hoje */}
          {/* <Tooltip
            title={
              !whatsAppConnected
                ? "WhatsApp desconectado"
                : "Dispara mensagens para TODAS as notificações de hoje pendentes"
            }
          >
            <span>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleTriggerTodayDispatch}
                disabled={!whatsAppConnected || loadingDispatch}
                size={isMobile || isSmallScreen ? "small" : "medium"}
              >
                {loadingDispatch ? "Disparando..." : "Disparar Hoje"}
              </Button>
            </span>
          </Tooltip> */}

          {/* Estatísticas de Disparo do Dia */}
          {dispatchStats && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                ml: "auto",
              }}
            >
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<CheckCircleIcon />}
                label={dispatchStats.sent}
              />
              <Chip
                size="small"
                color="error"
                variant="outlined"
                icon={<ErrorIcon />}
                label={dispatchStats.failed}
              />
              <Chip
                size="small"
                color="default"
                variant="outlined"
                icon={<PendingIcon />}
                label={dispatchStats.pending}
              />
            </Box>
          )}

          {!whatsAppConnected && (
            <Typography variant="caption" color="text.secondary">
              Conecte o WhatsApp para enviar mensagens
            </Typography>
          )}
        </Box>

        {/* Indicador de Filtros Ativos */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            px: { xs: 1, sm: 0 },
          }}
        >
          <Chip
            label={`Período: ${
              filters.startDate
                ? new Date(filters.startDate + "T12:00:00").toLocaleDateString(
                    "pt-BR"
                  )
                : ""
            } - ${
              filters.endDate
                ? (() => {
                    // Subtrai 1 dia da endDate para exibir a data correta
                    const displayDate = new Date(filters.endDate + "T12:00:00");
                    displayDate.setDate(displayDate.getDate() - 1);
                    return displayDate.toLocaleDateString("pt-BR");
                  })()
                : ""
            }`}
            color="primary"
            variant="outlined"
            size="small"
          />
          {showOnlyUnread && (
            <Chip
              label="Apenas não lidas"
              color="secondary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>

        {/* Lista de Notificações */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, mx: { xs: 1, sm: 0 } }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            maxHeight: isSmallScreen
              ? "calc(100vh - 250px)"
              : isMobile
                ? "calc(100vh - 320px)"
                : "none",
            minHeight: isSmallScreen
              ? "calc(100vh - 250px)"
              : isMobile
                ? "calc(100vh - 320px)"
                : "calc(100vh - 320px)",
            overflowY: isMobile || isSmallScreen ? "auto" : "visible",
            px: { xs: 1, sm: 0 },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography>Carregando...</Typography>
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <ScheduleIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                {showOnlyUnread && notifications.length > 0
                  ? "Nenhuma notificação não lida encontrada"
                  : "Nenhuma notificação encontrada"}
              </Typography>
              <Typography color="textSecondary" sx={{ mb: 2 }}>
                {showOnlyUnread && notifications.length > 0
                  ? "Todas as notificações foram lidas"
                  : "Crie sua primeira notificação agendada"}
              </Typography>
              {(!showOnlyUnread || notifications.length === 0) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateModal}
                >
                  Nova Notificação
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={isSmallScreen ? 1.5 : 3}>
              {paginatedNotifications.map((notification) => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={notification.id}>
                  <Card
                    sx={{
                      height: isSmallScreen ? 260 : 290,
                      display: "flex",
                      flexDirection: "column",
                      border: notification.isRead ? "none" : "2px solid",
                      borderColor: notification.isRead
                        ? "transparent"
                        : "primary.main",
                      bgcolor: isOverdue(
                        notification.scheduledDate,
                        notification.scheduledTime
                      )
                        ? "error.light"
                        : isUpcoming(
                              notification.scheduledDate,
                              notification.scheduledTime
                            )
                          ? "warning.light"
                          : "background.paper",
                    }}
                  >
                    <CardContent
                      sx={{ flexGrow: 1, p: isSmallScreen ? 1.5 : 2 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant={isSmallScreen ? "body1" : "h6"}
                          component="h3"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            flex: 1,
                            mr: 1,
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                          {!notification.isRead && (
                            <Chip size="small" color="primary" label="Nova" />
                          )}
                          {isUpcoming(
                            notification.scheduledDate,
                            notification.scheduledTime
                          ) && (
                            <Chip
                              size="small"
                              color="warning"
                              label="Próxima"
                            />
                          )}
                          {isOverdue(
                            notification.scheduledDate,
                            notification.scheduledTime
                          ) && (
                            <Chip size="small" color="error" label="Atrasada" />
                          )}
                          {/* Indicador de status WhatsApp */}
                          {notification.whatsappSent && (
                            <Tooltip
                              title={`Enviado em ${notification.whatsappSentAt ? new Date(notification.whatsappSentAt).toLocaleString("pt-BR") : ""}`}
                            >
                              <Chip
                                size="small"
                                color="success"
                                variant="filled"
                                icon={<CheckCircleIcon />}
                                label="Enviado"
                              />
                            </Tooltip>
                          )}
                          {!notification.whatsappSent &&
                            notification.whatsappError && (
                              <Tooltip title={notification.whatsappError}>
                                <Chip
                                  size="small"
                                  color="error"
                                  variant="filled"
                                  icon={<ErrorIcon />}
                                  label="Erro"
                                />
                              </Tooltip>
                            )}
                          {!notification.whatsappSent &&
                            !notification.whatsappError &&
                            hasWhatsAppPhone(notification) && (
                              <Chip
                                size="small"
                                color="default"
                                variant="outlined"
                                icon={<PendingIcon />}
                                label="Pendente"
                              />
                            )}
                        </Box>
                      </Box>

                      {/* Exibir quem criou a notificação */}
                      {notification.user && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            mb: 1,
                            fontStyle: "italic",
                          }}
                        >
                          Criado por:{" "}
                          {notification.user.name ||
                            notification.user.email ||
                            "Usuário"}
                        </Typography>
                      )}

                      <Typography
                        variant={isSmallScreen ? "caption" : "body2"}
                        color="textSecondary"
                        paragraph
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {notification.summary}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography
                          variant={isSmallScreen ? "caption" : "body2"}
                          color="textSecondary"
                        >
                          {formatDateTime(
                            notification.scheduledDate,
                            notification.scheduledTime
                          )}
                        </Typography>
                      </Box>

                      {notification.pet && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <PetsIcon fontSize="small" color="action" />
                          <Typography
                            variant={isSmallScreen ? "caption" : "body2"}
                            color="textSecondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            <strong>Pet:</strong> {notification.pet.name}
                            {notification.pet.tutor &&
                              ` (${getTutorName(notification.pet.tutor)})`}
                          </Typography>
                        </Box>
                      )}

                      {notification.details && (
                        <Typography
                          variant={isSmallScreen ? "caption" : "body2"}
                          color="textSecondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          <strong>Detalhes:</strong> {notification.details}
                        </Typography>
                      )}
                    </CardContent>

                    <CardActions
                      sx={{
                        justifyContent: "space-between",
                        px: isSmallScreen ? 1 : 2,
                        pb: isSmallScreen ? 1 : 2,
                        pt: isSmallScreen ? 0.5 : 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {/* Checkbox para seleção de WhatsApp em massa */}
                        {hasWhatsAppPhone(notification) && (
                          <Tooltip
                            title={
                              whatsAppConnected
                                ? "Selecionar para WhatsApp"
                                : "WhatsApp desconectado"
                            }
                          >
                            <span>
                              <Checkbox
                                size="small"
                                checked={selectedForWhatsApp.includes(
                                  notification.id
                                )}
                                onChange={() =>
                                  handleToggleWhatsAppSelection(notification.id)
                                }
                                disabled={!whatsAppConnected}
                                sx={{
                                  p: 0.5,
                                  opacity: whatsAppConnected ? 1 : 0.5,
                                }}
                              />
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Visualizar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenViewModal(notification)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditModal(notification)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteNotification(notification.id)
                            }
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                        {/* Botão WhatsApp individual */}
                        {hasWhatsAppPhone(notification) && (
                          <Tooltip
                            title={
                              whatsAppConnected
                                ? "Enviar WhatsApp"
                                : "WhatsApp desconectado"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenSingleWhatsApp(notification)
                                }
                                color="success"
                                disabled={!whatsAppConnected}
                              >
                                <WhatsAppIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                      <Tooltip
                        title={
                          notification.isRead
                            ? "Marcar como não lida"
                            : "Marcar como lida"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleToggleRead(notification)}
                          color={notification.isRead ? "default" : "primary"}
                        >
                          {notification.isRead ? <UnreadIcon /> : <ReadIcon />}
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Paginação fixa abaixo do conteúdo */}
        {!loading &&
          filteredNotifications.length > 0 &&
          totalNotificationsPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                mb: 2,
                px: { xs: 1, sm: 0 },
              }}
            >
              <Pagination
                count={totalNotificationsPages}
                page={notificationsPage}
                onChange={(_, value) => setNotificationsPage(value)}
                color="primary"
                size={isMobile || isSmallScreen ? "small" : "large"}
                showFirstButton={!isMobile && !isSmallScreen}
                showLastButton={!isMobile && !isSmallScreen}
                siblingCount={isMobile || isSmallScreen ? 0 : 1}
                boundaryCount={isMobile || isSmallScreen ? 1 : 1}
              />
            </Box>
          )}
      </Box>

      {/* Modal de Criação */}
      <Dialog
        open={openCreateModal}
        onClose={handleCloseModals}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Nova Notificação</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
            <TextField
              label="Título *"
              fullWidth
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />
            <TextField
              label="Resumo *"
              fullWidth
              multiline
              rows={2}
              value={formData.summary}
              onChange={(e) =>
                setFormData({ ...formData, summary: e.target.value })
              }
              helperText="Breve descrição da notificação"
            />
            <TextField
              label="Detalhes"
              fullWidth
              multiline
              rows={4}
              value={formData.details}
              onChange={(e) =>
                setFormData({ ...formData, details: e.target.value })
              }
              helperText="Informações adicionais (opcional)"
            />
            <Autocomplete
              fullWidth
              options={filteredPets}
              loading={false}
              getOptionLabel={(option) => option.name || ""}
              value={selectedPet}
              onChange={(event, newValue) => {
                setSelectedPet(newValue);
                setFormData({ ...formData, petId: newValue?.id || "" });
              }}
              inputValue={petInputValue}
              onInputChange={(event, newInputValue, reason) => {
                setPetInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pet (opcional)"
                  placeholder="Digite o nome do pet para buscar..."
                  helperText="Deixe em branco para notificação geral"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <PetsIcon color="action" sx={{ mr: 1 }} />
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body1">{option.name}</Typography>
                    {option.breed && (
                      <Typography variant="caption" color="text.secondary">
                        Raça: {option.breed}
                      </Typography>
                    )}
                    {option.tutor && (
                      <Typography variant="caption" color="text.secondary">
                        Tutor: {getTutorName(option.tutor)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              noOptionsText="Nenhum pet encontrado"
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Data *"
                type="date"
                value={formData.scheduledDate}
                onChange={(e) =>
                  setFormData({ ...formData, scheduledDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Horário *"
                type="time"
                value={formData.scheduledTime}
                onChange={(e) =>
                  setFormData({ ...formData, scheduledTime: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModals}>Cancelar</Button>
          <Button
            onClick={handleCreateNotification}
            variant="contained"
            startIcon={<AddIcon />}
          >
            Criar Notificação
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog
        open={openEditModal}
        onClose={handleCloseModals}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editar Notificação</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
            <TextField
              label="Título *"
              fullWidth
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />
            <TextField
              label="Resumo *"
              fullWidth
              multiline
              rows={2}
              value={formData.summary}
              onChange={(e) =>
                setFormData({ ...formData, summary: e.target.value })
              }
            />
            <TextField
              label="Detalhes"
              fullWidth
              multiline
              rows={4}
              value={formData.details}
              onChange={(e) =>
                setFormData({ ...formData, details: e.target.value })
              }
            />
            <Autocomplete
              fullWidth
              options={filteredPets}
              loading={false}
              getOptionLabel={(option) => option.name || ""}
              value={selectedPet}
              onChange={(event, newValue) => {
                setSelectedPet(newValue);
                setFormData({ ...formData, petId: newValue?.id || "" });
              }}
              inputValue={petInputValue}
              onInputChange={(event, newInputValue, reason) => {
                setPetInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pet (opcional)"
                  placeholder="Digite o nome do pet para buscar..."
                  helperText="Deixe em branco para notificação geral"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <PetsIcon color="action" sx={{ mr: 1 }} />
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body1">{option.name}</Typography>
                    {option.breed && (
                      <Typography variant="caption" color="text.secondary">
                        Raça: {option.breed}
                      </Typography>
                    )}
                    {option.tutor && (
                      <Typography variant="caption" color="text.secondary">
                        Tutor: {getTutorName(option.tutor)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              noOptionsText="Nenhum pet encontrado"
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Data *"
                type="date"
                value={formData.scheduledDate}
                onChange={(e) =>
                  setFormData({ ...formData, scheduledDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Horário *"
                type="time"
                value={formData.scheduledTime}
                onChange={(e) =>
                  setFormData({ ...formData, scheduledTime: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModals}>Cancelar</Button>
          <Button
            onClick={handleUpdateNotification}
            variant="contained"
            startIcon={<EditIcon />}
          >
            Salvar Alterações
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Visualização */}
      <Dialog
        open={openViewModal}
        onClose={handleCloseModals}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {selectedNotification?.title}
            <Box sx={{ display: "flex", gap: 1 }}>
              {selectedNotification && !selectedNotification.isRead && (
                <Chip size="small" color="primary" label="Nova" />
              )}
              {selectedNotification &&
                isUpcoming(
                  selectedNotification.scheduledDate,
                  selectedNotification.scheduledTime
                ) && <Chip size="small" color="warning" label="Próxima" />}
              {selectedNotification &&
                isOverdue(
                  selectedNotification.scheduledDate,
                  selectedNotification.scheduledTime
                ) && <Chip size="small" color="error" label="Atrasada" />}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Resumo
                </Typography>
                <Typography>{selectedNotification.summary}</Typography>
              </Box>

              {/* Exibir quem criou a notificação */}
              {selectedNotification.user && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Criado por
                  </Typography>
                  <Typography>
                    {selectedNotification.user.name ||
                      selectedNotification.user.email ||
                      "Usuário"}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Data
                    </Typography>
                    <Typography>
                      {new Date(
                        selectedNotification.scheduledDate
                      ).toLocaleDateString("pt-BR")}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TimeIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Horário
                    </Typography>
                    <Typography>
                      {selectedNotification.scheduledTime}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {selectedNotification.pet && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PetsIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Pet
                    </Typography>
                    <Typography>
                      {selectedNotification.pet.name}
                      {selectedNotification.pet.breed &&
                        ` (${selectedNotification.pet.breed})`}
                      {selectedNotification.pet.tutor &&
                        ` - Tutor: ${getTutorName(selectedNotification.pet.tutor)}`}
                    </Typography>
                  </Box>
                </Box>
              )}

              {selectedNotification.details && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Detalhes
                  </Typography>
                  <Typography>{selectedNotification.details}</Typography>
                </Box>
              )}

              <Box>
                <Typography variant="body2" color="textSecondary">
                  Criado em:{" "}
                  {new Date(selectedNotification.createdAt).toLocaleString(
                    "pt-BR"
                  )}
                </Typography>
                {selectedNotification.updatedAt !==
                  selectedNotification.createdAt && (
                  <Typography variant="body2" color="textSecondary">
                    Atualizado em:{" "}
                    {new Date(selectedNotification.updatedAt).toLocaleString(
                      "pt-BR"
                    )}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModals}>Fechar</Button>
          {selectedNotification && (
            <>
              <Button
                onClick={() => {
                  handleCloseModals();
                  handleOpenEditModal(selectedNotification);
                }}
                startIcon={<EditIcon />}
              >
                Editar
              </Button>
              <Button
                onClick={() => handleToggleRead(selectedNotification)}
                startIcon={
                  selectedNotification.isRead ? <UnreadIcon /> : <ReadIcon />
                }
              >
                {selectedNotification.isRead
                  ? "Marcar como não lida"
                  : "Marcar como lida"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Modal de Filtros */}
      <Dialog
        open={openFilterModal}
        onClose={() => setOpenFilterModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Filtros de Notificações</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Selecione o período de datas e opções de exibição
            </Typography>

            <TextField
              label="Data Inicial"
              type="date"
              value={tempStartDate}
              onChange={(e) => setTempStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="Data Final"
              type="date"
              value={tempEndDate}
              onChange={(e) => setTempEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Switch
                checked={tempShowOnlyUnread}
                onChange={(e) => setTempShowOnlyUnread(e.target.checked)}
              />
              <Typography>Exibir apenas notificações não lidas</Typography>
            </Box>

            <Alert severity="info" sx={{ mt: 1 }}>
              Por padrão, são exibidas as notificações do dia atual. Use os
              filtros para visualizar outras datas.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearFilters} color="secondary">
            Limpar Filtros
          </Button>
          <Button onClick={() => setOpenFilterModal(false)}>Cancelar</Button>
          <Button onClick={handleApplyFilters} variant="contained">
            Aplicar Filtros
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de WhatsApp Individual */}
      <Dialog
        open={openSingleWhatsAppModal}
        onClose={() => setOpenSingleWhatsAppModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WhatsAppIcon color="success" />
          Enviar WhatsApp
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {whatsAppSingleMessage.notification && (
              <>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Notificação
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {whatsAppSingleMessage.notification.title}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Telefone
                  </Typography>
                  <Typography variant="body1">
                    {whatsAppSingleMessage.phone}
                  </Typography>
                </Box>
              </>
            )}
            <TextField
              label="Mensagem"
              multiline
              rows={8}
              fullWidth
              value={whatsAppSingleMessage.text}
              onChange={(e) =>
                setWhatsAppSingleMessage((prev) => ({
                  ...prev,
                  text: e.target.value,
                }))
              }
              helperText="Você pode editar a mensagem antes de enviar"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSingleWhatsAppModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSendSingleWhatsApp}
            disabled={sendingWhatsApp || !whatsAppSingleMessage.text}
            startIcon={
              sendingWhatsApp ? <CircularProgress size={20} /> : <SendIcon />
            }
          >
            {sendingWhatsApp ? "Enviando..." : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de WhatsApp em Massa */}
      <Dialog
        open={openWhatsAppModal}
        onClose={() => setOpenWhatsAppModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WhatsAppIcon color="success" />
          Enviar WhatsApp em Massa ({whatsAppMessages.length} mensagens)
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Revise as mensagens abaixo. Você pode editar cada uma
              individualmente antes de enviar.
            </Alert>
            <List sx={{ maxHeight: 400, overflow: "auto" }}>
              {whatsAppMessages.map((msg, index) => (
                <Box key={msg.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    sx={{
                      flexDirection: "column",
                      alignItems: "stretch",
                      py: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {msg.tutorName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Pet: {msg.petName} | Tel: {msg.phone}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={msg.notificationTitle}
                        variant="outlined"
                      />
                    </Box>
                    <TextField
                      size="small"
                      multiline
                      rows={3}
                      fullWidth
                      value={msg.text}
                      onChange={(e) =>
                        handleUpdateBulkMessage(msg.id, e.target.value)
                      }
                    />
                  </ListItem>
                </Box>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWhatsAppModal(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSendBulkWhatsApp}
            disabled={sendingWhatsApp || whatsAppMessages.length === 0}
            startIcon={
              sendingWhatsApp ? <CircularProgress size={20} /> : <SendIcon />
            }
          >
            {sendingWhatsApp
              ? "Enviando..."
              : `Enviar ${whatsAppMessages.length} Mensagens`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Notifications;
